/**
 * Inline email typo detection — Baymard-preferred alternative to a "Confirm email"
 * field. Catches the ~90% of email typos that are domain-level mistakes (gnail.com,
 * gmai.com, hotmial.com, .con/.cm TLDs) without adding form friction.
 *
 * Returns a suggested corrected email if the domain is within edit-distance ≤ 2 of
 * a popular provider (and not an exact match), otherwise null. The caller chooses
 * how to present — this util makes no UI decisions.
 *
 * Critical for Sharpr: email verification is off in Supabase + card is charged on
 * day 6, so a typo'd email creates an unrecoverable account with a live charge.
 * This closes the typo gap without the conversion hit of a second email field.
 */

// Top consumer providers covering the overwhelming majority of real-world typos.
// Ordered for readability; editDistance iteration is O(n) over this whole list.
const POPULAR_DOMAINS = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'msn.com',
  'live.com',
  'proton.me',
  'protonmail.com',
  'comcast.net',
  'verizon.net',
  'att.net',
  'sbcglobal.net',
  'ymail.com',
  'mail.com',
  'gmx.com',
  'zoho.com',
];

const MAX_EDIT_DISTANCE = 2;

// Iterative Levenshtein distance — classic DP, two-row optimization.
// Kept local so this util has zero runtime deps.
function editDistance(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const prev = new Array(b.length + 1);
  const curr = new Array(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;

  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        curr[j - 1] + 1, // insertion
        prev[j] + 1, // deletion
        prev[j - 1] + cost, // substitution
      );
    }
    for (let j = 0; j <= b.length; j++) prev[j] = curr[j];
  }

  return prev[b.length];
}

/**
 * Suggest a corrected email if the domain portion looks like a typo of a popular
 * provider. Returns null when the email is malformed, already exact, or too far
 * from any known domain to be a confident suggestion.
 */
export function suggestEmailFix(email: string): string | null {
  if (!email) return null;
  const trimmed = email.trim().toLowerCase();
  const atIdx = trimmed.lastIndexOf('@');
  // Need a local part, an @, and a domain with at least one dot.
  if (atIdx <= 0 || atIdx >= trimmed.length - 1) return null;

  const local = trimmed.slice(0, atIdx);
  const domain = trimmed.slice(atIdx + 1);
  if (!domain.includes('.')) return null;

  // Exact match on a popular domain — no suggestion.
  if (POPULAR_DOMAINS.includes(domain)) return null;

  // Find the closest popular domain; require a strictly positive edit distance
  // so identical strings never match (already handled above, but belt-and-brace).
  let best: { domain: string; distance: number } | null = null;
  for (const candidate of POPULAR_DOMAINS) {
    const d = editDistance(domain, candidate);
    if (d > 0 && d <= MAX_EDIT_DISTANCE && (!best || d < best.distance)) {
      best = { domain: candidate, distance: d };
    }
  }

  return best ? `${local}@${best.domain}` : null;
}
